import {
  FileInfo,
  FileStatus,
  ErrorCode,
  ProcessError,
  ValidationResult,
  CONSTANTS
} from '../types';

/**
 * 文件处理工具类
 * 提供文件验证、信息生成、预览等功能
 */
export class FileUtils {
  /**
   * 生成唯一的文件ID
   */
  static generateFileId(): string {
    return `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 验证单个文件
   */
  static validateFile(file: File): ValidationResult {
    try {
      // 检查文件大小
      if (file.size > CONSTANTS.MAX_FILE_SIZE) {
        return {
          valid: false,
          error: {
            code: ErrorCode.FILE_TOO_LARGE,
            message: `文件 "${file.name}" 大小超出限制，最大支持 ${this.formatFileSize(CONSTANTS.MAX_FILE_SIZE)}`,
            recoverable: false,
            timestamp: Date.now(),
            details: { fileSize: file.size, maxSize: CONSTANTS.MAX_FILE_SIZE }
          }
        };
      }

      // 检查文件格式（MIME类型）
      if (!CONSTANTS.SUPPORTED_FORMATS.includes(file.type)) {
        return {
          valid: false,
          error: {
            code: ErrorCode.UNSUPPORTED_FORMAT,
            message: `文件 "${file.name}" 格式不支持，支持格式：JPG、PNG、WEBP`,
            recoverable: false,
            timestamp: Date.now(),
            details: { fileType: file.type, supportedTypes: CONSTANTS.SUPPORTED_FORMATS }
          }
        };
      }

      // 检查文件扩展名
      const extension = this.getFileExtension(file.name);
      if (extension && !CONSTANTS.SUPPORTED_EXTENSIONS.includes(extension.toLowerCase())) {
        return {
          valid: false,
          error: {
            code: ErrorCode.UNSUPPORTED_FORMAT,
            message: `文件 "${file.name}" 扩展名不支持，支持扩展名：${CONSTANTS.SUPPORTED_EXTENSIONS.join(', ')}`,
            recoverable: false,
            timestamp: Date.now(),
            details: { extension, supportedExtensions: CONSTANTS.SUPPORTED_EXTENSIONS }
          }
        };
      }

      // 检查文件名长度
      if (file.name.length > 255) {
        return {
          valid: false,
          error: {
            code: ErrorCode.PROCESSING_ERROR,
            message: `文件 "${file.name}" 名称过长，请重命名后重试`,
            recoverable: true,
            timestamp: Date.now()
          }
        };
      }

      return { valid: true };

    } catch (error) {
      return {
        valid: false,
        error: {
          code: ErrorCode.UNKNOWN_ERROR,
          message: `验证文件 "${file.name}" 时发生错误`,
          recoverable: false,
          timestamp: Date.now(),
          details: error
        }
      };
    }
  }

  /**
   * 验证文件列表
   */
  static validateFileList(files: File[]): {
    validFiles: File[];
    invalidFiles: Array<{ file: File; error: ProcessError }>;
  } {
    const validFiles: File[] = [];
    const invalidFiles: Array<{ file: File; error: ProcessError }> = [];

    // 检查批量限制
    if (files.length > CONSTANTS.MAX_BATCH_SIZE) {
      const error: ProcessError = {
        code: ErrorCode.PROCESSING_ERROR,
        message: `一次最多只能处理 ${CONSTANTS.MAX_BATCH_SIZE} 个文件`,
        recoverable: false,
        timestamp: Date.now(),
        details: { fileCount: files.length, maxBatchSize: CONSTANTS.MAX_BATCH_SIZE }
      };

      // 只保留前面的有效数量
      files.slice(0, CONSTANTS.MAX_BATCH_SIZE).forEach(file => {
        const validation = this.validateFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          invalidFiles.push({ file, error: validation.error! });
        }
      });

      // 超出数量的文件标记为错误
      files.slice(CONSTANTS.MAX_BATCH_SIZE).forEach(file => {
        invalidFiles.push({ file, error });
      });
    } else {
      // 验证每个文件
      files.forEach(file => {
        const validation = this.validateFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          invalidFiles.push({ file, error: validation.error! });
        }
      });
    }

    return { validFiles, invalidFiles };
  }

  /**
   * 创建FileInfo对象
   */
  static async createFileInfo(file: File, generatePreview = true): Promise<FileInfo> {
    const fileInfo: FileInfo = {
      id: this.generateFileId(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      status: FileStatus.PENDING,
      progress: 0
    };

    if (generatePreview) {
      try {
        fileInfo.preview = await this.generatePreviewUrl(file);
      } catch (error) {
        console.warn(`生成预览失败: ${file.name}`, error);
        // 预览生成失败不影响文件处理
      }
    }

    return fileInfo;
  }

  /**
   * 批量创建FileInfo对象
   */
  static async createFileInfoList(files: File[], generatePreview = true): Promise<FileInfo[]> {
    const createPromises = files.map(file => this.createFileInfo(file, generatePreview));
    return Promise.all(createPromises);
  }

  /**
   * 生成预览URL
   */
  static generatePreviewUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('预览生成失败'));
        }
      };

      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * 清理预览URL
   */
  static cleanupPreviewUrl(url: string): void {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  /**
   * 获取文件扩展名
   */
  static getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot > 0 ? fileName.substring(lastDot) : '';
  }

  /**
   * 获取不带扩展名的文件名
   */
  static getFileNameWithoutExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot > 0 ? fileName.substring(0, lastDot) : fileName;
  }

  /**
   * 格式化文件大小
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const sizes = Math.floor(Math.log(bytes) / Math.log(k));
    const size = (bytes / Math.pow(k, sizes)).toFixed(1);

    return `${size} ${units[sizes]}`;
  }

  /**
   * 获取文件类型描述
   */
  static getFileTypeDescription(file: File): string {
    switch (file.type) {
      case 'image/jpeg':
        return 'JPEG 图片';
      case 'image/png':
        return 'PNG 图片';
      case 'image/webp':
        return 'WebP 图片';
      default:
        return '未知格式';
    }
  }

  /**
   * 检查是否为图片文件
   */
  static isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * 检查文件是否支持
   */
  static isSupportedFile(file: File): boolean {
    return CONSTANTS.SUPPORTED_FORMATS.includes(file.type);
  }

  /**
   * 下载文件
   */
  static downloadFile(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    // 清理
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /**
   * 复制到剪贴板
   */
  static async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.warn('复制到剪贴板失败:', error);
      return false;
    }
  }

  /**
   * 检查浏览器功能支持
   */
  static checkBrowserSupport(): {
    canvas: boolean;
    fileApi: boolean;
    dragAndDrop: boolean;
    clipboard: boolean;
  } {
    return {
      canvas: !!document.createElement('canvas').getContext,
      fileApi: !!(window.File && window.FileReader && window.FileList && window.Blob),
      dragAndDrop: 'draggable' in document.createElement('div'),
      clipboard: !!navigator.clipboard
    };
  }

  /**
   * 从拖拽事件中提取文件
   */
  static extractFilesFromDragEvent(event: DragEvent | React.DragEvent<HTMLDivElement>): File[] {
    const files: File[] = [];

    if (event.dataTransfer?.items) {
      // 使用 DataTransferItemList 接口
      Array.from(event.dataTransfer.items).forEach(item => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      });
    } else if (event.dataTransfer?.files) {
      // 回退到 FileList 接口
      Array.from(event.dataTransfer.files).forEach(file => {
        files.push(file);
      });
    }

    return files;
  }

  /**
   * 从input元素中提取文件
   */
  static extractFilesFromInput(input: HTMLInputElement): File[] {
    return input.files ? Array.from(input.files) : [];
  }

  /**
   * 清理FileInfo资源
   */
  static cleanupFileInfo(fileInfo: FileInfo): void {
    if (fileInfo.preview) {
      this.cleanupPreviewUrl(fileInfo.preview);
    }
  }

  /**
   * 批量清理FileInfo资源
   */
  static cleanupFileInfoList(fileInfos: FileInfo[]): void {
    fileInfos.forEach(this.cleanupFileInfo);
  }

  /**
   * 获取文件的MIME类型（备用检测）
   */
  static getMimeTypeFromExtension(fileName: string): string {
    const extension = this.getFileExtension(fileName).toLowerCase();

    const mimeMap: Record<string, string> = {
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.png': 'image/png',
      '.webp': 'image/webp'
    };

    return mimeMap[extension] || 'application/octet-stream';
  }

  /**
   * 生成安全的文件名
   */
  static sanitizeFileName(fileName: string): string {
    // 移除或替换不安全的字符
    // eslint-disable-next-line no-control-regex
    return fileName.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_').trim();
  }
}